import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Polls the count of `unbooked` jobs whose scheduled date is today or
 * overdue so the sidebar Jobs tab can flash an amber warning badge.
 * Same shared-poll pattern as the other badge hooks.
 */
const listeners = new Set();
let sharedCount = 0;
let pollTimer = null;
let inflight = false;
let activeToken = null;

function broadcast(next) {
  sharedCount = next;
  listeners.forEach((cb) => cb(next));
}

async function fetchCountOnce() {
  if (inflight) return;
  inflight = true;
  try {
    const { data } = await api.get("/overdue-jobs/count");
    broadcast(Number(data?.count || 0));
  } catch (_) { /* keep previous on transient error */ }
  finally { inflight = false; }
}

function startPolling() {
  if (pollTimer) return;
  fetchCountOnce();
  pollTimer = setInterval(fetchCountOnce, 60_000);  // 1 min — dates change on a slower cadence
}

function stopPolling() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
  sharedCount = 0;
  activeToken = null;
}

export function useOverdueJobs() {
  const { user } = useAuth();
  const [count, setCount] = useState(sharedCount);

  useEffect(() => {
    const eligible = user && (user.role === "admin" || user.role === "office");
    if (!eligible) {
      if (activeToken) stopPolling();
      return;
    }
    const token = user.id;
    if (activeToken && activeToken !== token) stopPolling();
    activeToken = token;
    listeners.add(setCount);
    setCount(sharedCount);
    startPolling();
    return () => {
      listeners.delete(setCount);
      if (listeners.size === 0) stopPolling();
    };
  }, [user?.id, user?.role]);

  return { count, refresh: fetchCountOnce };
}
